import { RoundedButton } from '@/components/rounded-button';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ButtonProps, Heading, Icon, List, Text, VStack } from '@chakra-ui/react';
import { BsQuestionCircle } from 'react-icons/bs';

export const AirdropFaqModal = (buttonStyleProps?: ButtonProps) => {
  return (
    <DialogRoot size="lg" placement="center">
      <DialogTrigger asChild>
        <RoundedButton {...buttonStyleProps}>
          <Icon as={BsQuestionCircle} boxSize={4} />
          Airdrop FAQ
        </RoundedButton>
      </DialogTrigger>
      <DialogContent
        maxH="80vh"
        overflowY="auto"
        maxW={{ base: '100%', md: '540px' }}
        fontFamily="Lufga, sans-serif"
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '10px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0, 0, 0, 0.3)',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:active': {
            background: 'rgba(0, 0, 0, 0.4)',
            backgroundClip: 'content-box',
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
        }}
      >
        <DialogHeader
          display="unset"
          position="sticky"
          top={0}
          bg="white"
          borderBottom="1px solid"
          borderColor="border.subtle"
          mb={4}
          py={8}
        >
          <Heading fontSize={{ base: '3xl', md: '40px' }} textAlign="center" fontWeight={700} mb={0}>
            Airdrop FAQ
          </Heading>
          <DialogCloseTrigger
            rounded="full"
            border="1px solid"
            borderColor="border"
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
          />
        </DialogHeader>
        <DialogBody spaceY={6} px={8} lineHeight={1.25}>
          <VStack alignItems="stretch" spaceY={6}>
            {/* What is the Claim Party? */}
            <VStack alignItems="stretch" spaceY={3} borderBottom="1px solid" borderColor="border" pb={6}>
              <Heading fontSize="18px" fontWeight={500}>
                What is the Claim Party?
              </Heading>
              <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                The Claim Party is a 90-day period where ~100 Season1 wallets are grouped inside a private leaderboard.
                Earn Plume Points by using mainnet. During each weekly snapshot, a limited set of top contributors
                unlock their full Season1 Base Allocation + PLUME Boost ahead of schedule. Any wallets that haven't
                unlocked by Day 90 receive their allocation automatically.
              </Text>
            </VStack>

            {/* How do I speedrun my claim? */}
            <VStack alignItems="stretch" spaceY={3} borderBottom="1px solid" borderColor="border" pb={6}>
              <Heading fontSize="18px" fontWeight={500}>
                How do I speedrun my claim?
              </Heading>
              <List.Root gap={1} as="ol" pl={4}>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Bridge
                    </Text>{' '}
                    - move funds to Plume and start using dApps.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Rack up PP
                    </Text>{' '}
                    - every on-chain action pushes you up your party's board, across TVL, transactions, staking,
                    referrals and more.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Stay consistent
                    </Text>{' '}
                    - weekly snapshots verify your consistency and reward you even more.
                  </Text>
                </List.Item>
              </List.Root>
            </VStack>

            {/* How many wallets in my party can unlock early? */}
            <VStack alignItems="stretch" spaceY={3} borderBottom="1px solid" borderColor="border" pb={6}>
              <Heading fontSize="18px" fontWeight={500}>
                How many wallets in my party can unlock early?
              </Heading>
              <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                Every wallet in the claim party will get access by 90 days, early unlocks are based on the PP
                leaderboard and meeting usage thresholds. Each week we open slots for the most active wallets that meet
                (or beat) our minimum bar. This lets us scale distribution safely while rewarding genuine users, even if
                a party ends up low-activity it keeps things fair for everyone.
              </Text>
            </VStack>

            {/* What if my party is full of whales, do I stand a chance? */}
            <VStack alignItems="stretch" spaceY={3} borderBottom="1px solid" borderColor="border" pb={6}>
              <Heading fontSize="18px" fontWeight={500}>
                What if my party is full of whales, do I stand a chance?
              </Heading>
              <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                The PP mechanism is built to be fair to everyone, steady grinders and whales. It rewards genuine users.
                When wallets graduate the claim party each week, they are removed from the group. That means there will
                be more room for everyone else to make progress each week. Keep building PP; consistency often beats
                one-time size.
              </Text>
            </VStack>

            {/* Do my Claim Party points also count for Season 2? */}
            <VStack alignItems="stretch" spaceY={3}>
              <Heading fontSize="18px" fontWeight={500}>
                Do my Claim Party points also count for Season 2?
              </Heading>
              <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                Yes. Every PP you earn counts both toward your Claim Party rank and toward your share of Season 2
                rewards.
              </Text>
            </VStack>
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};
